// ── Dropdown entity shapes ──────────────────────────────────────

import {
  AssistantCreateApi,
  AssistantFrameApi,
  AssistantOptionsApi,
} from '../Services/AgentApiProviderTypes';

export interface IdNameEntity {
  id: string;
  name: string;
}

export interface KeyNameEntity {
  key: string;
  name: string;
}

export interface DictionaryEntity {
  id: string;
  title: string;
}

export interface PromptEntity extends IdNameEntity {
  pattern_type?: string;
}

// ── UI-only value types (for options in dropdowns) ──────────────

export type ModelName =
  | 'gpt-4.1-mini'
  | 'gpt-4.1'
  | 'gpt-5-mini'
  | 'gpt-5'
  | 'gpt-5.1'
  | 'gpt-5.2'
  | 'gpt-5.4';

// ── Client-side form state ──────────────────────────────────────

export interface AgentConfig {
  name: string;
  isActive: boolean;
  agentType: string;
  modelName: string | null;
  reasoning: string | null;
  // Left null for the models that take no service tier, and for the ones that
  // do but are being left on their project default.
  serviceTier: string | null;
  temperature: number;
  detailsLevel: string | undefined;
  presets: string[];
  promptIds: string[];
  noFramePromptIds: string[];
  selectedVoice: string;
  memoryType: string | null;
  memoryLimit: number | null;
  skills: string[];
  frameIds: string[];
  markerIds: string[];
  inlineSkillKeys: string[];
  fragmentKeys: string[];
  swarmKeys: string[];
  notificationIds: string[];
  documentCollectionIds: string[];
  informationPackageCollectionIds: string[];
  connectionIds: string[];
  catalog: string | null | undefined;
  dictionaryId: string | null;
  templateIds: string[] | null;
  auditExecution: boolean;
  nextAgentKey: string | null;
}

// ── Reference data for dropdowns ────────────────────────────────

export interface AgentReferenceData {
  prompts: PromptEntity[];
  // Only the frames this agent is linked to — the full catalogue is paged
  // through the frames picker instead of being preloaded.
  selectedFrames: AssistantFrameApi[];
  markers: IdNameEntity[];
  inlineSkills: KeyNameEntity[];
  fragments: KeyNameEntity[];
  notifications: IdNameEntity[];
  documentCollections: IdNameEntity[];
  informationPackageCollections: IdNameEntity[];
  connections: IdNameEntity[];
  catalogs: IdNameEntity[];
  dictionaries: DictionaryEntity[];
  swarms: KeyNameEntity[];
  templates: IdNameEntity[];
  // Agents that can be chained after this one — every agent in the
  // organization except the one being edited.
  agents: KeyNameEntity[];
  options: AssistantOptionsApi | null;
}

// ── Feature flags / permissions ─────────────────────────────────

export interface AgentPermissions {
  isDisabled: boolean;
  isOrganizationsPermission: boolean;
  isDocumentPermission: boolean;
  disableCollections: boolean;
  collectionTextWarning: string;
}

export interface AgentFeatureFlags {
  isDocumentFlag?: boolean;
  isProductFlag?: boolean;
  isInformationPackageFlag?: boolean;
  isNotificationFlag?: boolean;
  isVoiceFlag?: boolean;
  isConnectionFlag?: boolean;
}

// ── Pure helpers ────────────────────────────────────────────────

export function formatFrameType(frameType: string | undefined): string {
  if (frameType === 'CRITERIA') return 'Criteria Based';
  if (frameType === 'THEMATIC') return 'Thematics Based';
  return 'Topic Based';
}

export function getCollectionDisableReason(
  agentType: string | undefined,
  skills: string[],
  reasoning: string | null,
): string | null {
  if (agentType !== 'RAG') return null;
  if (skills?.some((s) => s === 'SKILL:WEB_SEARCH' || s === 'SKILL:WEB_SEARCH_HIGH')) {
    return 'Web search does not support Collections';
  }
  if (reasoning === 'minimal') {
    return 'Minimal reasoning does not support Collections';
  }
  return null;
}

export function configToPayload(c: AgentConfig): AssistantCreateApi {
  return {
    name: c.name,
    prompt_keys: c.promptIds,
    details_level: c.detailsLevel,
    presets: c.presets,
    temperature: c.temperature,
    notification_keys: c.notificationIds,
    document_collection_keys: c.documentCollectionIds,
    information_package_collection_keys: c.informationPackageCollectionIds,
    connections: c.connectionIds,
    frame_keys: c.frameIds,
    marker_keys: c.markerIds,
    inline_skill_keys: c.inlineSkillKeys,
    fragment_keys: c.fragmentKeys,
    no_frame_prompt_keys: c.frameIds.length > 0 ? c.noFramePromptIds : [],
    is_active: c.isActive,
    assistant_type: c.agentType,
    memory_type: c.memoryType,
    memory_limit: c.memoryLimit,
    catalog_key: c.catalog,
    taxonomy_dictionary_id: c.dictionaryId,
    skills: c.skills,
    model_name: c.modelName,
    reasoning: c.reasoning,
    service_tier: c.serviceTier,
    swarm: c.swarmKeys,
    templates: c.templateIds,
    audit_execution: c.auditExecution,
    next_assistant_key: c.nextAgentKey,
  };
}

// ── Defaults ────────────────────────────────────────────────────

// What the backend gives an agent that has never stored presets, so a new
// agent starts out the same as every agent created before them.
export const DEFAULT_PRESETS: string[] = ['TODAY', 'DETAILS_LEVEL'];

export const INITIAL_CONFIG: AgentConfig = {
  name: '',
  isActive: false,
  agentType: undefined as unknown as string,
  modelName: null,
  reasoning: null,
  serviceTier: null,
  temperature: 30,
  detailsLevel: undefined,
  presets: DEFAULT_PRESETS,
  promptIds: [],
  noFramePromptIds: [],
  selectedVoice: 'cedar',
  memoryType: null,
  memoryLimit: null,
  skills: [],
  frameIds: [],
  markerIds: [],
  inlineSkillKeys: [],
  fragmentKeys: [],
  swarmKeys: [],
  notificationIds: [],
  documentCollectionIds: [],
  informationPackageCollectionIds: [],
  connectionIds: [],
  catalog: undefined,
  dictionaryId: null,
  templateIds: null,
  auditExecution: true,
  nextAgentKey: null,
};

export const EMPTY_DATA: AgentReferenceData = {
  prompts: [],
  selectedFrames: [],
  markers: [],
  inlineSkills: [],
  fragments: [],
  notifications: [],
  documentCollections: [],
  informationPackageCollections: [],
  connections: [],
  catalogs: [],
  dictionaries: [],
  swarms: [],
  templates: [],
  agents: [],
  options: null,
};

// ── Agent page tabs ─────────────────────────────────────────────

export const AGENT_TABS = {
  SETTINGS: 'settings',
  NOTES: 'notes',
  INSTRUCTIONS: 'instructions',
  AUDIT: 'audit',
  METRICS: 'metrics',
} as const;

export type AgentTab = (typeof AGENT_TABS)[keyof typeof AGENT_TABS];

export const AGENT_TAB_LABELS: Record<AgentTab, string> = {
  settings: 'Settings',
  notes: 'Notes',
  instructions: 'Instructions',
  audit: 'Audit',
  metrics: 'Metrics',
};

export const isAgentTab = (value: string | null): value is AgentTab =>
  Object.values<string>(AGENT_TABS).includes(value ?? '');

// The agent page opens on Settings; every other tab is addressed by ?tab=
export const agentTabPath = (organizationId: string, agentId: string, tab: AgentTab): string =>
  `/organizations/${organizationId}/agents-hub/${agentId}` +
  (tab === AGENT_TABS.SETTINGS ? '' : `?tab=${tab}`);
